import * as XLSX from 'xlsx';
import { ROLE_OPTIONS } from '@/constants/roles';

/** Blank row shape for manual employee-entry forms (Add Employee, Onboarding). */
export const makeEmptyEmployee = () => ({
  name: '',
  email: '',
  team: '',
  timeZone: '',
  role: '',
  deskId: '',
  employeeId: '',
});

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const VALID_ROLES = ROLE_OPTIONS.map((o) => o.value);

/** Recognized column header spellings when parsing an uploaded CSV/Excel file. */
export const HEADER_ALIASES = {
  name: 'name',
  'full name': 'name',
  fullname: 'name',
  email: 'email',
  'e-mail': 'email',
  department: 'team',
  team: 'team',
  dept: 'team',
  role: 'role',
  timezone: 'timeZone',
  'time zone': 'timeZone',
  time_zone: 'timeZone',
  deskid: 'deskId',
  'desk id': 'deskId',
  desk_id: 'deskId',
  employeeid: 'employeeId',
  'employee id': 'employeeId',
  employee_id: 'employeeId',
};

export const normalizeRole = (val) => {
  const trimmed = String(val || '').trim();
  return (
    VALID_ROLES.find((r) => r.toLowerCase() === trimmed.toLowerCase()) ||
    trimmed
  );
};

/**
 * Row-level validation for parsed employee rows. Returns each row with an
 * `_errors` array (flat messages, for a preview table that just needs
 * pass/fail) and a `_fieldErrors` map (fieldName -> short caption, for
 * highlighting the specific invalid input in a form row) attached.
 */
export const validateCsvRows = (rows) => {
  const emailSet = new Set();
  const deskIdSet = new Set();

  return rows.map((row) => {
    const errors = [];
    const fieldErrors = {};

    if (!row.name?.trim()) {
      errors.push('Name is required');
      fieldErrors.name = 'Required';
    }

    if (!row.email?.trim()) {
      errors.push('Email is required');
      fieldErrors.email = 'Required';
    } else if (!EMAIL_REGEX.test(row.email.trim())) {
      errors.push('Invalid email format');
      fieldErrors.email = 'Invalid format';
    } else if (emailSet.has(row.email.trim().toLowerCase())) {
      errors.push('Duplicate email');
      fieldErrors.email = 'Duplicate';
    }
    if (row.email?.trim()) emailSet.add(row.email.trim().toLowerCase());

    if (!row.role?.trim()) {
      errors.push('Role is required');
      fieldErrors.role = 'Required';
    } else if (!VALID_ROLES.includes(normalizeRole(row.role))) {
      errors.push('Role must be Member, Manager or Admin');
      fieldErrors.role = 'Invalid';
    }

    if (!row.timeZone?.trim()) {
      errors.push('Timezone is required');
      fieldErrors.timeZone = 'Required';
    }

    if (row.deskId?.trim()) {
      if (deskIdSet.has(row.deskId.trim())) {
        errors.push('Duplicate Desk ID');
        fieldErrors.deskId = 'Duplicate';
      }
      deskIdSet.add(row.deskId.trim());
    }

    return { ...row, _errors: errors, _fieldErrors: fieldErrors };
  });
};

/**
 * Row-level validation variant that also requires a Department/Team value —
 * used where each row is assigned to its own team (e.g. onboarding, which
 * has no teams yet and creates one per unique value).
 */
export const validateCsvRowsWithTeam = (rows) =>
  validateCsvRows(rows).map((row) => {
    if (!row.team?.trim()) {
      return {
        ...row,
        _errors: ['Department is required', ...row._errors],
        _fieldErrors: { team: 'Required', ...row._fieldErrors },
      };
    }
    return row;
  });

/**
 * Parses a CSV/XLSX/XLS file into employee row objects using `HEADER_ALIASES`
 * to recognize common header spellings. Does not validate — pair with
 * `validateCsvRows`/`validateCsvRowsWithTeam`.
 */
export const parseFileToRows = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: '',
        });

        if (!raw.length) {
          return reject(new Error('The file appears to be empty.'));
        }

        const headers = raw[0].map(
          (h) =>
            HEADER_ALIASES[String(h).trim().toLowerCase()] ||
            String(h).trim().toLowerCase()
        );

        const dataRows = raw
          .slice(1)
          .filter((row) => row.some((cell) => String(cell).trim() !== ''));

        const employees = dataRows.map((row) => {
          const emp = makeEmptyEmployee();
          headers.forEach((key, i) => {
            if (key in emp) emp[key] = String(row[i] ?? '').trim();
          });
          if (emp.role) emp.role = normalizeRole(emp.role);
          return emp;
        });

        resolve(employees);
      } catch {
        reject(
          new Error(
            'Could not parse the file. Please check it is a valid CSV or Excel file.'
          )
        );
      }
    };
    reader.onerror = () => reject(new Error('Failed to read the file.'));
    reader.readAsBinaryString(file);
  });
